import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll } from "framer-motion";
import "./WhySection.css";

const ITEMS = [
  {
    n: "01",
    problemLines: ["요구사항만 전달하고 개발만 맡기면,", "비즈니스 목표와 어긋난 결과가 나옵니다."],
    solutionAccent: "기획부터",
    solutionMain: "참여합니다.",
    detailLines: ["단순 외주가 아닌,", "비즈니스 컨설팅 관점에서 함께합니다."],
  },
  {
    n: "02",
    problemLines: ["범용 템플릿에 맞추면 처음은 빠르지만,", "곧 한계에 부딪힙니다."],
    solutionAccent: "맞춤형 개발로,",
    solutionMain: "만듭니다.",
    detailLines: ["템플릿이 아닌,", "비즈니스에 최적화된 구조로 개발합니다."],
  },
  {
    n: "03",
    problemLines: ["PC 위주로 만들면", "모바일·태블릿에서 이탈과 성능 문제가 생깁니다."],
    solutionAccent: "반응형",
    solutionMain: "최적화를 적용합니다.",
    detailLines: ["모든 디바이스에서 일관된 경험과", "성능을 보장합니다."],
  },
  {
    n: "04",
    problemLines: ["콘텐츠 수정마다 개발자에게 요청하면", "운영 속도가 느려집니다."],
    solutionAccent: "관리자",
    solutionMain: "기능을 제공합니다.",
    detailLines: ["운영자가 직접 콘텐츠와 데이터를", "관리할 수 있는 CMS."],
  },
  {
    n: "05",
    problemLines: ["AI 기능은 넣었지만,", "실제 업무 흐름과 연결되지 않는 경우가 많습니다."],
    solutionAccent: "AI 시스템",
    solutionMain: "연동이 가능합니다.",
    detailLines: ["LLM·자동화 워크플로우를", "핵심 기능에 통합합니다."],
  },
  {
    n: "06",
    problemLines: ["서비스가 커질수록 구조 한계로", "속도 저하와 장애가 반복됩니다."],
    solutionAccent: "확장 가능한",
    solutionMain: "구조를 설계합니다.",
    detailLines: ["트래픽과 기능이 늘어나도", "흔들리지 않는 구조."],
  },
  {
    n: "07",
    problemLines: ["오픈 후 유지보수 없이 방치되면,", "작은 오류도 비즈니스 손실로 이어집니다."],
    solutionAccent: "지속적인",
    solutionMain: "유지보수를 이어갑니다.",
    detailLines: ["출시 이후에도 함께 성장하는", "운영 파트너십."],
  },
  {
    n: "08",
    problemLines: ["산업·규모를 고려하지 않은 디지털 전략은", "실행 단계에서 막힙니다."],
    solutionAccent: "기업 맞춤",
    solutionMain: "컨설팅을 제안합니다.",
    detailLines: ["산업·규모에 맞춘", "디지털 전환 전략을 제안합니다."],
  },
];

const SCENE_COUNT = 1 + ITEMS.length;

function WhyLines({ lines, className }) {
  return (
    <p className={className}>
      {lines.map((line, index) => (
        <span key={line}>
          {line}
          {index < lines.length - 1 ? <br /> : null}
        </span>
      ))}
    </p>
  );
}

function WhyIntroScene() {
  return (
    <div data-testid="why-intro" className="ds-why-scene ds-why-scene--dark">
      <div className="ds-why-scene__label">Why DSITESOLUTION — 05</div>
      <h2 className="ds-why-scene__title font-display font-kr mt-8 font-extrabold tracking-tight leading-[1.05]">
        왜{" "}
        <span className="ds-why-scene__muted">디사이트솔루션</span>
        인가.
      </h2>
      <p className="ds-why-scene__intro font-kr mt-8 md:mt-10 leading-relaxed max-w-3xl mx-auto">
        많은 프로젝트가 같은 지점에서 막힙니다.
        <br />
        문제를 먼저 짚고, 해결까지 이어갑니다.
      </p>
    </div>
  );
}

function WhyItemScene({ item }) {
  return (
    <article data-testid={`why-card-${item.n}`} className="ds-why-scene ds-why-scene--dark">
      <div className="ds-why-scene__number font-display font-semibold tracking-[0.2em]">{item.n}</div>
      <WhyLines
        lines={item.problemLines}
        className="ds-why-scene__problem font-kr mt-6 leading-relaxed max-w-3xl mx-auto"
      />
      <h3 className="ds-why-scene__solution font-display font-kr mt-8 font-extrabold tracking-tight leading-[1.05]">
        <span className="ds-why-scene__muted">{item.solutionAccent}</span>
        <br />
        {item.solutionMain}
      </h3>
      <WhyLines
        lines={item.detailLines}
        className="ds-why-scene__detail font-kr mt-8 leading-relaxed max-w-2xl mx-auto"
      />
    </article>
  );
}

function WhyStaticList() {
  return (
    <section className="relative bg-black text-white py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 ds-dot-grid opacity-40" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 text-center">
        <div className="max-w-4xl mx-auto">
          <WhyIntroScene />
        </div>
        <div className="mt-16 md:mt-24 max-w-4xl mx-auto space-y-16 pt-12 border-t border-white/10">
          {ITEMS.map((item) => (
            <WhyItemScene key={item.n} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyScrollSequence() {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  useEffect(() => {
    return scrollYProgress.on("change", (v) => {
      const idx = Math.min(SCENE_COUNT - 1, Math.max(0, Math.floor(v * SCENE_COUNT)));
      setActiveIndex(idx);
    });
  }, [scrollYProgress]);

  const activeItem = activeIndex > 0 ? ITEMS[activeIndex - 1] : null;

  return (
    <div
      id="why"
      data-testid="why-section"
      ref={containerRef}
      className="ds-why-sequence"
      style={{ height: `${SCENE_COUNT * 100}svh` }}
    >
      <div className="ds-why-sequence__sticky relative bg-black text-white overflow-hidden">
        <div className="absolute inset-0 ds-dot-grid opacity-40" aria-hidden="true" />
        <div className="absolute -bottom-32 -left-32 w-[60vw] h-[60vw] ds-glow opacity-50" aria-hidden="true" />

        <div className="relative max-w-6xl mx-auto w-full px-6 md:px-10 lg:px-16 h-full flex flex-col items-center justify-center text-center">
          <div className="w-full max-w-5xl min-h-[360px] md:min-h-[420px] flex flex-col justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
              >
                {activeIndex === 0 ? <WhyIntroScene /> : <WhyItemScene item={activeItem} />}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="ds-why-sequence__progress" aria-hidden="true">
            {Array.from({ length: SCENE_COUNT }, (_, i) => (
              <span
                key={i}
                className={`ds-why-sequence__dot${i === activeIndex ? " is-active" : ""}`}
              />
            ))}
          </div>

          <div className="ds-why-sequence__counter font-display">
            {activeIndex === 0 ? "Intro" : activeItem?.n}
            <span className="ds-why-scene__muted"> / 08</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WhySection() {
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduceMotion(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  if (reduceMotion) {
    return (
      <div id="why" data-testid="why-section">
        <WhyStaticList />
      </div>
    );
  }

  return <WhyScrollSequence />;
}
